import React, { useState, useRef } from 'react'
import Icons from '../../../Icons'
import { lightTheme } from '@/customTheme'
import WrapPopover, { PopoverWidthType } from './WrapPopover'
import PopoverSubheader from './DownloadPopover/PopoverSubheader'
import WithPopover, { WithPopoverHandle } from '../../WithPopover'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DropdownOption {
  id: number
  value: string
  label: string
  description?: string
  isPremium?: boolean
}

type DropdownPlacement = 'bottom' | 'top'

// ─── DropdownTrigger ──────────────────────────────────────────────────────────

interface DropdownTriggerProps {
  label: string
  isOpen: boolean
  disabled?: boolean
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void
}

function DropdownTrigger({ label, isOpen, disabled, onClick }: DropdownTriggerProps) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        background: disabled ? lightTheme.colors.grayScale50 : lightTheme.colors.white,
        border: `1px solid ${isOpen ? lightTheme.colors.grayScale300 : lightTheme.colors.grayScale100}`,
        borderRadius: '8px',
        padding: '8px 8px 8px 12px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxSizing: 'border-box',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span
        style={{
          ...lightTheme.typography.Small14regular,
          color: lightTheme.colors.blackGray,
          flex: 1,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icons.Drop />
      </div>
    </div>
  )
}

// ─── DropdownRow ──────────────────────────────────────────────────────────────

interface DropdownRowProps {
  option: DropdownOption
  isSelected: boolean
  onSelect: () => void
}

function DropdownRow({ option, isSelected, onSelect }: DropdownRowProps) {
  const [hovered, setHovered] = useState(false)
  const [active, setActive] = useState(false)

  const bg = active
    ? lightTheme.colors.grayScale100
    : hovered
    ? lightTheme.colors.grayScale50
    : 'transparent'

  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setActive(false) }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        padding: '8px',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
        background: bg,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
        <span
          style={{
            ...lightTheme.typography.Small14regular,
            color: lightTheme.colors.blackGray,
            textTransform: 'uppercase',
          }}
        >
          {option.label}
        </span>
        {option.description && (
          <span
            style={{
              ...lightTheme.typography.Small11medium,
              color: lightTheme.colors.grayScale400,
            }}
          >
            {option.description}
          </span>
        )}
      </div>
      <div
        style={{
          width: 24,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {isSelected ? (
          <Icons.CheckIcon fill={lightTheme.colors.primary} />
        ) : option.isPremium ? (
          <Icons.PremiumIcon />
        ) : null}
      </div>
    </div>
  )
}

// ─── PopoverDropdown ──────────────────────────────────────────────────────────

interface PopoverDropdownProps {
  label?: string
  options: DropdownOption[]
  selectedValue: string
  onChange: (value: string) => void
  onPremiumClick?: (value: string) => void
  width?: PopoverWidthType
  placement?: DropdownPlacement
  disabled?: boolean
}

function PopoverDropdown({
  label,
  options,
  selectedValue,
  onChange,
  onPremiumClick,
  width = 262,
  placement = 'bottom',
  disabled = false,
}: PopoverDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const withPopoverRef = useRef<WithPopoverHandle>(null)

  const selectedOption = options.find(o => o.value === selectedValue)

  const dropdown = (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <WithPopover
        ref={withPopoverRef}
        onOpenChange={setIsOpen}
        popoverContent={(buttonRef, closePopover) => (
          <WrapPopover
            buttonRef={buttonRef}
            placement={placement}
            width={width}
            padding={4}
            borderRadius={16}
            gap={0}
          >
            <div
              onMouseDown={(e) => e.stopPropagation()}
              style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            >
              {options.map(option => {
                const isSelected = option.value === selectedValue
                return (
                  <DropdownRow
                    key={option.id}
                    option={option}
                    isSelected={isSelected}
                    onSelect={() => {
                      if (option.isPremium && !isSelected) {
                        onPremiumClick?.(option.value)
                      } else {
                        onChange(option.value)
                        closePopover()
                      }
                    }}
                  />
                )
              })}
            </div>
          </WrapPopover>
        )}
      >
        <DropdownTrigger
          label={selectedOption?.label ?? selectedValue}
          isOpen={isOpen}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation()
            withPopoverRef.current?.togglePopover()
          }}
        />
      </WithPopover>
    </div>
  )

  if (!label) return dropdown

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <PopoverSubheader label={label} />
      {dropdown}
    </div>
  )
}

export default PopoverDropdown